import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  FormHelperText,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faSyringe as faSyringeIcon,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faVial,
  faCalendar,
  faCapsules,
} from "@fortawesome/free-solid-svg-icons";
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";      
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

const VaccineProductionReceiveForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    TransactionID: transactionId || "RC-XXXX-XX-XX",
    ReceiveDate: new Date(),
    WarehouseID: "",
    BudgetType: "Distribution", // Default เป็น งบจำหน่าย
    BudgetYear: "",
    ReceiveType: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: profile?.Email || "",
    ReceiveDetail: "",
    ProductionOrderNoted: "",
    Status: "Pending",
    VaccineDetails: [],
  });

  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [newDetail, setNewDetail] = useState({
    VaccineID: "",
    LotID: "",
    LocationID: "",
    RequestQuantity: "",
    ProductionDate: null,
    ExpiryDate: null,
    QCQuantity: "",
    QCAmountPassed:"",
    QCAmountFailed:"",
    QCResultPassed: true,
    QCResultRemark: "",
    ProductionRemark: "",
    ReceiveDate: formData.ReceiveDate,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!transactionId);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState(null);

  const getDoseCountValue = useCallback(
    (detail) => {
      const parsedDose = Number(detail?.DoseCount);
      if (!Number.isNaN(parsedDose) && parsedDose >= 0) {
        return parsedDose;
      }
      const packageSize =
        Number(
          vaccineOptions.find((vaccine) => vaccine.VaccineID === detail?.VaccineID)?.PackageSize
        ) || 0;
      const requestQuantity = Number(detail?.RequestQuantity) || 0;
      if (packageSize > 0 && requestQuantity > 0) {
        return Math.ceil(requestQuantity * packageSize);
      }
      return 0;
    },
    [vaccineOptions]
  );

  const vaccineSummary = useMemo(() => {
    const details = formData.VaccineDetails || [];
    const totalItems = details.length;
    const uniqueVaccineIds = Array.from(
      new Set(details.map((detail) => detail?.VaccineID).filter((id) => !!id))
    );
    const totalBottles = details.reduce(
      (sum, detail) => sum + (Number(detail?.RequestQuantity) || 0),
      0
    );
    const totalDoses = details.reduce(
      (sum, detail) => sum + getDoseCountValue(detail),
      0
    );
    return {
      totalItems,
      uniqueVaccineIds,
      totalBottles,
      totalDoses,
    };
  }, [formData.VaccineDetails, getDoseCountValue]);

  // Sync newDetail.ReceiveDate with formData.ReceiveDate
  useEffect(() => {
    setNewDetail((prev) => ({ ...prev, ReceiveDate: formData.ReceiveDate }));
  }, [formData.ReceiveDate]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchReceive = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineProductionReceivesByID",
        data: { TransactionID: id },
        sessionId: sessionId || "",
      });
      console.log("Response from /getVaccineProductionReceivesByID:", response);
      if (response.result === "success") {
        const receive = response.VaccineProductionReceive;
        const details = response.VaccineDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          const packageSize = vaccine?.PackageSize || 10;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          const productionDate = detail.ProductionDate || null;
          const expiryDate = detail.ExpiryDate  || null;
          const receiveDate = detail.ReceiveDate || null;
          /*const productionDate = detail.ProductionDate
            ? new Date(detail.ProductionDate)
            : null;
          const expiryDate = detail.ExpiryDate ? new Date(detail.ExpiryDate) : null;
          const receiveDate = detail.ReceiveDate
            ? new Date(detail.ReceiveDate)
            : null;*/
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            LocationID: detail.LocationID || "",
            RequestQuantity: detail.RequestQuantity,
            ProductionDate: productionDate,
            ExpiryDate: expiryDate,
            QCQuantity: detail.QCQuantity || "",
            QCAmountPassed: detail.QCAmountPassed || "",
            QCAmountFailed: detail.QCAmountFailed || "",
            QCResultPassed:
              detail.QCResultPassed !== undefined ? detail.QCResultPassed : true,
            QCResultRemark: detail.QCResultRemark || "",
            ProductionRemark: String(detail.ProductionRemark || ""),
            ReceiveDate: receiveDate,
            DoseCount: doseCount,
          };
        });
        setFormData({
          TransactionID: receive.TransactionID,
          ReceiveDate: new Date(receive.ReceiveDate),
          WarehouseID: receive.WarehouseID,
          BudgetType: receive.BudgetType,
          BudgetYear: receive.BudgetYear || "",
          ReceiveType: receive.ReceiveType,
          ReferenceDocumentType: receive.ReferenceDocumentType,
          ReferenceDocumentNo: receive.ReferenceDocumentNo,
          WarehouseStaffID: receive.WarehouseStaffID,
          ReceiveDetail: receive.ReceiveDetail,
          ProductionOrderNoted: receive.ProductionOrderNoted || "",
          Status: receive.Status,
          VaccineDetails: updatedDetails,
        });
      }
    } catch (error) {
      console.error("Error fetching receive:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchWarehouses();
        const vaccines = await fetchVaccineOptions();
        if (transactionId) {
          await fetchReceive(transactionId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, transactionId]);

  const formatDateToThai = (dateString) => {
  // 1. ตรวจสอบว่ามีค่าส่งเข้ามาหรือไม่ ถ้าไม่มีให้ส่งค่าว่างกลับไป
  if (!dateString) {
    return "";
  }

  try {
    // 2. แปลง String เป็น Date Object
    const date = new Date(dateString);

    // 3. ตรวจสอบว่าแปลงเป็นวันที่สำเร็จหรือไม่ (ป้องกันกรณี dateString ไม่ถูกต้อง)
    if (isNaN(date.getTime())) {
      return "";
    }

    // 4. จัดรูปแบบเป็น 'วัน/เดือน/ปี' แบบไทย (พ.ศ.)
    return date.toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      calendar: 'buddhist',
    });
  } catch (error) {
    // หากเกิดข้อผิดพลาดในการแปลง ให้ส่งค่าว่างกลับไป
    console.error("Invalid date format:", dateString, error);
    return "";
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (e) => {
  const { name, value } = e.target; // value คือ string 'yyyy-MM-dd'
  // แปลง string กลับเป็น Date object ก่อนบันทึกลง state
  // new Date() สามารถ parse string รูปแบบ 'yyyy-MM-dd' ได้ถูกต้อง
  const dateObject = value ? new Date(value) : null;
  setFormData({ ...formData, [name]: dateObject });
};

  // Function to sanitize Lot No. - only allow alphanumeric, /, -, _ and no spaces
  const sanitizeLotID = (value) => {
    // Remove spaces and allow only: A-Z, a-z, 0-9, /, -, _
    return value.replace(/\s/g, '').replace(/[^A-Za-z0-9/\-_]/g, '');
  };

  const handleDetailInputChange = (e) => {
    const { name, value } = e.target;
    // Apply sanitization for LotID
    if (name === 'LotID') {
      const sanitizedValue = sanitizeLotID(value);
      setNewDetail({ ...newDetail, [name]: sanitizedValue });
    } else {
      setNewDetail({ ...newDetail, [name]: value });
    }
  };

  const handleDetailDateChange = (name, date) => {
    setNewDetail({ ...newDetail, [name]: date });
  };

  const handleDetailCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setNewDetail({ ...newDetail, [name]: checked });
  };

  const handleQuantityChange = (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 10;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return { ...detail, RequestQuantity: newQuantity, DoseCount: newDoseCount };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleQCQuantityChange = (vaccineId, lotId, value) => {
    const newQCQuantity = value === "" ? null : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, QCQuantity: newQCQuantity };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };
  
  const handleQCAmountPassedChange = (vaccineId, lotId, value) => {
    const newQCAmountPassed = value === "" ? null : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, QCAmountPassed: newQCAmountPassed };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  
  const handleQCAmountFailedChange = (vaccineId, lotId, value) => {
    const newQCAmountFailed = value === "" ? null : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, QCAmountFailed: newQCAmountFailed };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleQCResultChange = (vaccineId, lotId, checked) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, QCResultPassed: checked };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleRemarkChange = (vaccineId, lotId, field, value) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, [field]: field === 'ProductionRemark' ? String(value) : value };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleLocationChange = (vaccineId, lotId, field, value) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        return { ...detail, [field]: value };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const handleLotChange = (vaccineId, oldLotId, newLotId) => {
    // Sanitize LotID input
    const sanitizedLotId = sanitizeLotID(newLotId);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === oldLotId) {
        return { ...detail, LotID: sanitizedLotId };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "WarehouseID":
      case "BudgetType":
      case "BudgetYear":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.WarehouseID &&
      formData.BudgetType &&
      formData.BudgetYear &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddDetail = () => {
    if (newDetail.VaccineID && newDetail.LotID && newDetail.RequestQuantity > 0) {
      if (
        newDetail.ProductionDate &&
        newDetail.ExpiryDate &&
        newDetail.ExpiryDate < newDetail.ProductionDate
      ) {
        alert("วันหมดอายุต้องไม่เร็วกว่าวันที่ผลิต");
        return;
      }
      const selectedVaccine = vaccineOptions.find(
        (v) => v.VaccineID === newDetail.VaccineID
      );
      if (selectedVaccine) {
        const packageSize = selectedVaccine.PackageSize || 10;
        const newQuantity = parseInt(newDetail.RequestQuantity);
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        const detailData = {
          VaccineID: newDetail.VaccineID,
          name: selectedVaccine.VaccineNameTH,
          LotID: newDetail.LotID,
          LocationID: newDetail.LocationID || "",
          RequestQuantity: newQuantity,
          ProductionDate: newDetail.ProductionDate,
          ExpiryDate: newDetail.ExpiryDate,
          QCQuantity: newDetail.QCQuantity ? parseInt(newDetail.QCQuantity) : null,
          QCAmountPassed: newDetail.QCAmountPassed ? parseInt(newDetail.QCAmountPassed) : null,
          QCAmountFailed: newDetail.QCAmountFailed ? parseInt(newDetail.QCAmountFailed) : null,
          QCResultPassed: newDetail.QCResultPassed,
          QCResultRemark: newDetail.QCResultRemark,
          ProductionRemark: String(newDetail.ProductionRemark || ""),
          ReceiveDate: newDetail.ReceiveDate || formData.ReceiveDate,
          DoseCount: newDoseCount,
        };
        setFormData((prevFormData) => ({
          ...prevFormData,
          VaccineDetails: [...prevFormData.VaccineDetails, detailData],
        }));
        setNewDetail({
          VaccineID: "",
          LotID: "",
          LocationID: "",
          RequestQuantity: "",
          ProductionDate: null,
          ExpiryDate: null,
          QCQuantity: "",
          QCAmountPassed: "",
          QCAmountFailed: "",
          QCResultPassed: true,
          QCResultRemark: "",
          ProductionRemark: "",
          ReceiveDate: formData.ReceiveDate,
        });
      } else {
        console.warn(`VaccineID ${newDetail.VaccineID} not found in vaccineOptions`);
      }
    } else {
      console.warn("Missing required fields:", newDetail);
    }
  };

  const handleRemoveDetail = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID || v.LotID !== row.LotID
      ),
    });
  };

  // Handle file upload
  const onDrop = useCallback((acceptedFiles) => {
    setFileError(null);
    const file = acceptedFiles[0];

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      setFileError('กรุณาอัปโหลดไฟล์ CSV หรือ Excel เท่านั้น');
      return;
    }

    const reader = new FileReader();

    if (file.name.endsWith('.xlsx')) {
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headers = jsonData[0];
          const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));

          const formattedData = rows.map(row => {
            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = row[index] !== undefined ? row[index] : '';
            });

            const vaccine = vaccineOptions.find(v => v.VaccineID === rowData.VaccineID);
            const packageSize = vaccine?.PackageSize || 10;
            const requestQuantity = parseInt(rowData.RequestQuantity) || 0;
            const doseCount = Math.ceil(requestQuantity * packageSize);

            return {
              VaccineID: rowData.VaccineID || '',
              name: vaccine ? vaccine.VaccineNameTH : 'ไม่พบชื่อวัคซีน',
              LotID: sanitizeLotID(rowData.LotID || ''),
              LocationID: rowData.LocationID || '',
              RequestQuantity: requestQuantity,
              ProductionDate: rowData.ProductionDate ? new Date(rowData.ProductionDate) : null,
              ExpiryDate: rowData.ExpiryDate ? new Date(rowData.ExpiryDate) : null,
              QCQuantity: parseInt(rowData.QCQuantity) || null,
              QCAmountPassed: parseInt(rowData.QCAmountPassed) || null,
              QCAmountFailed: parseInt(rowData.QCAmountFailed) || null,
              QCResultPassed: rowData.QCResultPassed === 1 || rowData.QCResultPassed === '1',
              QCResultRemark: String(rowData.QCResultRemark || ''),
              ProductionRemark: String(rowData.ProductionRemark || ''),
              ReceiveDate: rowData.ReceiveDate ? new Date(rowData.ReceiveDate) : formData.ReceiveDate,
              DoseCount: doseCount,
            };
          });

          console.log('Parsed Excel Data:', formattedData);
          setFormData(prev => ({
            ...prev,
            VaccineDetails: [...prev.VaccineDetails, ...formattedData],
          }));
        } catch (err) {
          setFileError('เกิดข้อผิดพลาดในการอ่านไฟล์ Excel: ' + err.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = (e) => {
        const text = e.target.result;
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: header => header.trim().replace(/^"|"$/g, ''),
          transform: (value, header) => {
            let cleaned = value.trim().replace(/^"|"$/g, '');
            if (['ProductionDate', 'ExpiryDate', 'ReceiveDate'].includes(header)) {
              return cleaned ? new Date(cleaned) : null;
            }
            if (['RequestQuantity', 'QCQuantity', 'QCAmountPassed', 'QCAmountFailed'].includes(header)) {
              return cleaned && !isNaN(cleaned) ? parseInt(cleaned) : 0;
            }
            if (header === 'QCResultPassed') {
              return cleaned === '1' || cleaned.toLowerCase() === 'true';
            }
            if (header === 'ProductionRemark' || header === 'QCResultRemark') {
              return String(cleaned || '');
            }
            return cleaned || '';
          },
          complete: (results) => {
            const formattedData = results.data.map(row => {
              const vaccine = vaccineOptions.find(v => v.VaccineID === row.VaccineID);
              const packageSize = vaccine?.PackageSize || 10;
              const requestQuantity = parseInt(row.RequestQuantity) || 0;
              const doseCount = Math.ceil(requestQuantity * packageSize);

              return {
                VaccineID: row.VaccineID || '',
                name: vaccine ? vaccine.VaccineNameTH : 'ไม่พบชื่อวัคซีน',
                LotID: sanitizeLotID(row.LotID || ''),
                LocationID: row.LocationID || '',
                RequestQuantity: requestQuantity,
                ProductionDate: row.ProductionDate,
                ExpiryDate: row.ExpiryDate,
                QCQuantity: parseInt(row.QCQuantity) || null,
                QCAmountPassed: parseInt(row.QCAmountPassed) || null,
                QCAmountFailed: parseInt(row.QCAmountFailed) || null,
                QCResultPassed: row.QCResultPassed,
                QCResultRemark: String(row.QCResultRemark || ''),
                ProductionRemark: String(row.ProductionRemark || ''),
                ReceiveDate: row.ReceiveDate || formData.ReceiveDate,
                DoseCount: doseCount,
              };
            });

            console.log('Parsed CSV Data:', formattedData);
            setFormData(prev => ({
              ...prev,
              VaccineDetails: [...prev.VaccineDetails, ...formattedData],
            }));
          },
          error: (err) => {
            setFileError('เกิดข้อผิดพลาดในการอ่านไฟล์ CSV: ' + err.message);
          }
        });
      };
      reader.readAsText(file);
    }
  }, [vaccineOptions, formData.ReceiveDate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    }
  });

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      /*
      const formattedReceiveDate = formData.ReceiveDate
        ? `${formData.ReceiveDate.getFullYear()}-${(
            formData.ReceiveDate.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}-${formData.ReceiveDate
            .getDate()
            .toString()
            .padStart(2, "0")}`
        : null;
      */
      
      const response = await apiService({
        method: "POST",
        url: "/createVaccineProductionReceives",
        //data: { ...formData, ReceiveDate: formattedReceiveDate, VaccineDetails: [] },
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, TransactionID: response.TransactionID });
        setIsSubmitted(true);
        alert("สร้างเอกสารสำเร็จ! เลขที่เอกสาร: " + response.TransactionID);
      }
    } catch (error) {
      console.error("Error creating receive:", error);
      alert("เกิดข้อผิดพลาดในการสร้างเอกสาร: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    /*const formattedReceiveDate = formData.ReceiveDate
      ? `${formData.ReceiveDate.getFullYear()}-${(
          formData.ReceiveDate.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${formData.ReceiveDate
          .getDate()
          .toString()
          .padStart(2, "0")}`
      : null;*/
    const submitData = {
      ...formData,
      ReceiveDate: formData.ReceiveDate,
      VaccineDetails: formData.VaccineDetails.map((detail) => ({
        ...detail,
        ProductionDate: detail.ProductionDate,
        ExpiryDate: detail.ExpiryDate,
        ReceiveDate: detail.ReceiveDate,
        ProductionRemark: String(detail.ProductionRemark || ''),
      })),
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineProductionReceivesByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบรับสำเร็จ!");
        navigate("/vaccineproductionreceivelist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditable = !isSubmitted;

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringeIcon} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faVial} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID,
      sortable: true,
      cell: (row) => (
        <Box>
          <Input
            value={row.LotID}
            onChange={(e) =>
              handleLotChange(row.VaccineID, row.LotID, e.target.value)
            }
            onBlur={(e) => {
              // Ensure value is sanitized on blur
              const sanitized = sanitizeLotID(e.target.value);
              if (sanitized !== e.target.value) {
                handleLotChange(row.VaccineID, row.LotID, sanitized);
              }
            }}
            pattern="[A-Za-z0-9/\-_]*"
            size="sm"
            width="120px"
            ml={10}
          />
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.RequestQuantity}
          onChange={(e) =>
            handleQuantityChange(row.VaccineID, row.LotID, e.target.value)
          }
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนโดส</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.DoseCount.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalendar} />
          <span>วันที่ผลิต</span>
        </HStack>
      ),
      selector: (row) => formatDateToThai(row.ProductionDate),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalendar} />
          <span>วันหมดอายุ</span>
        </HStack>
      ),
      selector: (row) => formatDateToThai(row.ExpiryDate),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalendar} />
          <span>วันที่รับ</span>
        </HStack>
      ),
      selector: (row) => formatDateToThai(row.ReceiveDate),
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faVial} />
          <span>ตำแหน่งที่จัดเก็บ</span>
        </HStack>
      ),
      selector: (row) => row.LocationID,
      sortable: true,
      cell: (row) => (
        <Input
          value={row.LocationID}
          onChange={(e) =>
            handleLocationChange(row.VaccineID, row.LotID, "LocationID", e.target.value)
          }
          size="sm"
          width="120px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faFileLines} />
          <span>อุณหภูมิจากฝ่ายผลิต</span>
        </HStack>
      ),
      selector: (row) => row.ProductionRemark,
      sortable: true,
      cell: (row) => (
        <Input
          value={row.ProductionRemark}
          onChange={(e) =>
            handleRemarkChange(
              row.VaccineID,
              row.LotID,
              "ProductionRemark",
              e.target.value
            )
          }
          size="sm"
          width="150px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>ผล QC</span>
        </HStack>
      ),
      selector: (row) => row.QCResultPassed,
      sortable: true,
      cell: (row) => (
        <Checkbox
          isChecked={row.QCResultPassed}
          onChange={(e) =>
            handleQCResultChange(row.VaccineID, row.LotID, e.target.checked)
          }
          ml={10}
        >
          ผ่าน
        </Checkbox>
      ),
      width: "150px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวนที่ส่งทดสอบ (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCQuantity,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.QCQuantity || ""}
          onChange={(e) =>
            handleQCQuantityChange(row.VaccineID, row.LotID, e.target.value)
          }
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวนที่ทดสอบผ่าน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCAmountPassed,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.QCAmountPassed || ""}
          onChange={(e) =>
            handleQCAmountPassedChange(row.VaccineID, row.LotID, e.target.value)
          }
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวนที่ทดสอบไม่ผ่าน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.QCAmountFailed,
      sortable: true,
      cell: (row) => (
        <Input
          type="number"
          value={row.QCAmountFailed || ""}
          onChange={(e) =>
            handleQCAmountFailedChange(row.VaccineID, row.LotID, e.target.value)
          }
          min={0}
          size="sm"
          width="100px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faFileLines} />
          <span>หมายเหตุ QC</span>
        </HStack>
      ),
      selector: (row) => row.QCResultRemark,
      sortable: true,
      cell: (row) => (
        <Input
          value={row.QCResultRemark}
          onChange={(e) =>
            handleRemarkChange(row.VaccineID, row.LotID, "QCResultRemark", e.target.value)
          }
          size="sm"
          width="150px"
          ml={10}
        />
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) => (
        <IconButton.Delete
          icon={faTrash}
          onClick={() => handleRemoveDetail(row)}
          label="ลบรายการ"
          width="100px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายการรับวัคซีนจากฝ่ายผลิต" />

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ข้อมูลเอกสารการรับวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่เอกสาร
              </FormLabel>
              <Input
                name="TransactionID"
                value={formData.TransactionID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่เอกสาร
              </FormLabel>
              <CustomDatePicker
                name="ReceiveDate"
                value={formData.ReceiveDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.WarehouseID}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                คลังวัคซีน
              </FormLabel>
              <Select
                name="WarehouseID"
                value={formData.WarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังวัคซีน"
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.WarehouseID}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.BudgetType}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                ประเภทวัคซีน
              </FormLabel>
              <Select
                name="BudgetType"
                value={formData.BudgetType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทวัคซีน"
                colorScheme="iBlue"
              >
                <option value="Prevention">งบป้องกัน</option>
                <option value="Distribution">งบจำหน่าย</option>
              </Select>
              <FormErrorMessage>{errors.BudgetType}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl
              isRequired
              isInvalid={!!errors.BudgetYear}
              isDisabled={isSubmitted}
            >
              <FormLabel fontSize="xl" color="gray">
                ปีงบประมาณ
              </FormLabel>
              <Input
                name="BudgetYear"
                value={formData.BudgetYear}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.BudgetYear}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทการรับ
              </FormLabel>
              <Input
                name="ReceiveType"
                value={formData.ReceiveType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                ประเภทเอกสารอ้างอิง
              </FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={formData.ReferenceDocumentType}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่เอกสารอ้างอิง
              </FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={formData.ReferenceDocumentNo}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เจ้าหน้าที่คลัง
              </FormLabel>
              <Input
                name="WarehouseStaffID"
                value={formData.WarehouseStaffID}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={3}></GridItem>
          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                บันทึกการสั่งผลิต
              </FormLabel>
              <Textarea
                name="ProductionOrderNoted"
                value={formData.ProductionOrderNoted}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={2}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                รายละเอียดเพิ่มเติม
              </FormLabel>
              <Textarea
                name="ReceiveDetail"
                value={formData.ReceiveDetail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
              </FormLabel>
              <Input
                type="hidden"
                name="Status"
                value={formData.Status}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel
                onClick={() => navigate("/vaccineproductionreceivelist")}
              />
              <IconButton.Submit label="สร้างเอกสาร" onClick={handleSubmit} />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
          title="เพิ่มรายการวัคซีนที่รับ"
          bgColor="iPurple.500"
          icon={faFileLines}
        />
            <Grid templateColumns="repeat(6, 1fr)" gap={6} p={5}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วัคซีน
                </FormLabel>
                <Select
                  name="VaccineID"
                  value={newDetail.VaccineID}
                  onChange={handleDetailInputChange}
                  placeholder="เลือกวัคซีน"
                  colorScheme="iPurple"
                >
                  {vaccineOptions.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  Lot No.
                </FormLabel>
                <Input
                  name="LotID"
                  value={newDetail.LotID}
                  onChange={handleDetailInputChange}
                  onBlur={(e) => {
                    // Ensure value is sanitized on blur
                    const sanitized = sanitizeLotID(e.target.value);
                    if (sanitized !== e.target.value) {
                      setNewDetail({ ...newDetail, LotID: sanitized });
                    }
                  }}
                  pattern="[A-Za-z0-9/\-_]*"
                  colorScheme="iPurple"
                />
                {/*
                <FormHelperText fontSize="sm" color="gray.500">
                ตัวเลข, A-Z, a-z, /, -, _ เท่านั้น
                </FormHelperText>
                */}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  จำนวน (ขวด)
                </FormLabel>
                <Input
                  name="RequestQuantity"
                  type="number"
                  value={newDetail.RequestQuantity}
                  onChange={handleDetailInputChange}
                  min={1}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วันที่ผลิต
                </FormLabel>
                <CustomDatePicker
                  name="ProductionDate"
                  value={newDetail.ProductionDate}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วันหมดอายุ
                </FormLabel>
                <CustomDatePicker
                  name="ExpiryDate"
                  value={newDetail.ExpiryDate}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วันที่รับ
                </FormLabel>
                <CustomDatePicker
                  name="ReceiveDate"
                  value={newDetail.ReceiveDate}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              {/*
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  ตำแหน่งที่จัดเก็บ
                </FormLabel>
                <Input
                  name="LocationID"
                  value={newDetail.LocationID}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
                */}
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  อุณหภูมิจากฝ่ายผลิต
                </FormLabel>
                <Input
                  name="ProductionRemark"
                  value={newDetail.ProductionRemark}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              <GridItem colSpan={5}></GridItem>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  ผลการทดสอบคุณภาพ
                </FormLabel>
                <Checkbox
                  name="QCResultPassed"
                  isChecked={newDetail.QCResultPassed}
                  onChange={handleDetailCheckboxChange}
                  colorScheme="iPurple"
                >
                  ผ่าน
                </Checkbox>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  จำนวนที่ส่งทดสอบ (ขวด)
                </FormLabel>
                <Input
                  name="QCQuantity"
                  type="number"
                  value={newDetail.QCQuantity}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  จำนวนที่ทดสอบผ่าน (ขวด)
                </FormLabel>
                <Input
                  name="QCAmountPassed"
                  type="number"
                  value={newDetail.QCAmountPassed}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  จำนวนที่ทดสอบไม่ผ่าน (ขวด)
                </FormLabel>
                <Input
                  name="QCAmountFailed"
                  type="number"
                  value={newDetail.QCAmountFailed}
                  onChange={handleDetailInputChange}
                  min={0}
                  colorScheme="iPurple"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  หมายเหตุการทดสอบคุณภาพ
                </FormLabel>
                <Input
                  name="QCResultRemark"
                  value={newDetail.QCResultRemark}
                  onChange={handleDetailInputChange}
                  colorScheme="iPurple"
                />
              </FormControl>
              <Box mt={10}>
                <IconButton.Add onClick={handleAddDetail} h={9} />
              </Box>
            </Grid>
            {/* File Upload Section */}
            <Box mt={6} p={5} borderWidth={1} borderRadius="md" bg="gray.50">
              <FormLabel fontSize="xl" color="gray">
                นำเข้าข้อมูลจากไฟล์ Excel/CSV
              </FormLabel>
              <Box
                {...getRootProps()}
                p={6}
                borderWidth={2}
                borderStyle="dashed"
                borderColor={isDragActive ? "iPurple.500" : "gray.300"}
                bg={isDragActive ? "iPurple.50" : "white"}
                borderRadius="md"
                textAlign="center"
                cursor="pointer"
              >
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>วางไฟล์ที่นี่...</p>
                ) : (
                  <p>ลากและวางไฟล์ Excel/CSV หรือคลิกเพื่อเลือกไฟล์</p>
                )}
              </Box>
              {fileError && (
                <Box mt={2} color="red.500" fontSize="sm">
                  {fileError}
                </Box>
              )}
            </Box>
          </Box>


          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <StandardHeader
              title="รายการวัคซีนที่รับ"
              bgColor="iOrange.500"
              icon={faTable}
            />
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={tableCustomStyles}
                noDataComponent={<Box p={5}>ไม่มีข้อมูลในตาราง</Box>}
              />
            </Box>
          </Box>

          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Heading size={{ base: "sm", md: "md" }} color="iPurple.600" mb={4}>
              สรุปข้อมูลการรับวัคซีน
            </Heading>
            <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }} gap={5}>
              <Box
                borderWidth={1}
                borderRadius="md"
                p={4}
                bg="gray.50"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Text fontWeight="medium" color="gray.600">
                  จำนวนรายการที่รับเข้า
                </Text>
                <Text fontWeight="semibold" fontSize="xl">
                  {vaccineSummary.totalItems.toLocaleString()} รายการ
                </Text>
              </Box>
              <Box
                borderWidth={1}
                borderRadius="md"
                p={4}
                bg="gray.50"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Text fontWeight="medium" color="gray.600">
                  ยอดรวมจำนวนขวด
                </Text>
                <Text fontWeight="semibold" fontSize="xl">
                  {vaccineSummary.totalBottles.toLocaleString()} ขวด
                </Text>
              </Box>
              <Box
                borderWidth={1}
                borderRadius="md"
                p={4}
                bg="gray.50"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Text fontWeight="medium" color="gray.600">
                  ชนิดวัคซีนที่รับเข้า
                </Text>
                <Text fontWeight="semibold" fontSize="xl">
                  {vaccineSummary.uniqueVaccineIds.length.toLocaleString()} ชนิด
                </Text>
              </Box>
              <Box
                borderWidth={1}
                borderRadius="md"
                p={4}
                bg="gray.50"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Text fontWeight="medium" color="gray.600">
                  ยอดรวมจำนวนโดส
                </Text>
                <Text fontWeight="semibold" fontSize="xl">
                  {vaccineSummary.totalDoses.toLocaleString()} โดส
                </Text>
              </Box>
            </Grid>
          </Box>


          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/vaccineproductionreceivelist")}
                label="ย้อนกลับ"
              />
              <IconButton.Save onClick={handleConfirm} isLoading={isLoading} />
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default VaccineProductionReceiveForm;